import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiCheckCircle, mdiCloseCircleOutline, mdiDownload, mdiLinkOff, mdiLockOutline } from '@mdi/js'
import type { SignerState } from '../../api/schema'
import { SignerApiError, signerApi } from '../../hooks/useSignatures'
import { loadPdfModule, type LoadedPdfModule } from '../documents/PdfPreview'
import { useFormatters } from '../../lib/format'
import { answerMfaWithPasskey, browserSupportsWebAuthn } from '../../lib/passkeys'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PageHeader } from '../shared/PageHeader'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import { Button } from '../ui/button'
import { Card, CardContent } from '../ui/card'
import { Checkbox } from '../ui/checkbox'
import { AppIcon } from '../ui/icon'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { SignaturePad } from './SignaturePad'
import { isHandwritingReady, tokenFromHash, type Handwriting } from './signature-utils'

const sessionKey = (orgCode: string) => `signature-session:${orgCode}`

type Phase = 'loading' | 'ready' | 'not_found' | 'expired'

/** Sets <meta name="referrer" content="no-referrer"> while the page is open. */
function useNoReferrer() {
    useEffect(() => {
        const meta = document.createElement('meta')
        meta.name = 'referrer'
        meta.content = 'no-referrer'
        document.head.appendChild(meta)
        return () => {
            document.head.removeChild(meta)
        }
    }, [])
}

/**
 * The page a signer lands on from the mailed link (spec 2026-10-05-05 §3).
 *
 * It lives outside routes/$org: the signer has no account here. The link
 * carries its token in the fragment (#t=...), which never reaches a server
 * log nor a Referer; the page exchanges it once for a short session, kept in
 * this tab's sessionStorage, and removes it from the address bar at once.
 * Then, in order: the whole PDF, the identification, the consent, the
 * handwritten signature, "Sign" or "Decline".
 */
export function SigningPage({ orgCode }: { orgCode: string }) {
    const { t, i18n } = useTranslation('signatures')
    const lang = i18n.language?.startsWith('en') ? 'en' : 'fr'
    useNoReferrer()
    const [phase, setPhase] = useState<Phase>('loading')
    const [session, setSession] = useState<string | null>(null)
    const [state, setState] = useState<SignerState | null>(null)
    const started = useRef(false)

    const adopt = useCallback(
        (s: string, st: SignerState) => {
            sessionStorage.setItem(sessionKey(orgCode), s)
            setSession(s)
            setState(st)
            setPhase('ready')
        },
        [orgCode],
    )

    const onError = useCallback((e: unknown) => {
        if (e instanceof SignerApiError && e.status === 401) {
            setPhase('expired')
            return true
        }
        if (e instanceof SignerApiError && e.status === 404) {
            setPhase('not_found')
            return true
        }
        return false
    }, [])

    useEffect(() => {
        if (started.current) return
        started.current = true
        const token = tokenFromHash(window.location.hash)
        if (token) {
            // The token leaves the address bar before anything else happens.
            window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search)
            signerApi
                .openSession(orgCode, token, lang)
                .then((res) => adopt(res.session, res.state))
                .catch((e) => {
                    if (!onError(e)) setPhase('not_found')
                })
            return
        }
        const stored = sessionStorage.getItem(sessionKey(orgCode))
        if (!stored) {
            setPhase('not_found')
            return
        }
        signerApi
            .state(orgCode, stored, lang)
            .then((st) => adopt(stored, st))
            .catch((e) => {
                if (!onError(e)) setPhase('not_found')
            })
    }, [orgCode, lang, adopt, onError])

    if (phase === 'loading') {
        return <div className="mx-auto max-w-3xl p-6 text-muted-foreground" data-testid="signing-loading">{t('signer.loading')}</div>
    }
    if (phase === 'not_found' || phase === 'expired' || !state || !session) {
        return (
            <div className="mx-auto max-w-xl space-y-3 p-6 text-center" data-testid={phase === 'expired' ? 'signing-expired' : 'signing-not-found'}>
                <AppIcon path={mdiLinkOff} size="lg" className="mx-auto text-muted-foreground" />
                <p className="text-lg font-semibold text-foreground">
                    {phase === 'expired' ? t('signer.expired_title') : t('signer.not_found_title')}
                </p>
                <p className="text-sm text-muted-foreground">
                    {phase === 'expired' ? t('signer.expired_body') : t('signer.not_found_body')}
                </p>
            </div>
        )
    }

    return (
        <SigningFlow
            orgCode={orgCode}
            session={session}
            state={state}
            lang={lang}
            onSession={adopt}
            onState={setState}
            onError={onError}
        />
    )
}

interface FlowProps {
    orgCode: string
    session: string
    state: SignerState
    lang: string
    onSession: (s: string, st: SignerState) => void
    onState: (st: SignerState) => void
    onError: (e: unknown) => boolean
}

function SigningFlow({ orgCode, session, state, lang, onSession, onState, onError }: FlowProps) {
    const { t } = useTranslation('signatures')
    const { formatDate } = useFormatters()
    const formatDateTime = (d: string) => formatDate(d, { dateStyle: 'short', timeStyle: 'short' })
    const [error, setError] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)

    const run = async (fn: () => Promise<void>) => {
        setBusy(true)
        setError(null)
        try {
            await fn()
        } catch (e) {
            if (!onError(e)) setError(e instanceof Error ? e.message : String(e))
        } finally {
            setBusy(false)
        }
    }

    const open = state.request_status === 'pending' && state.signer_status === 'pending'

    return (
        <div className="mx-auto max-w-6xl space-y-6 p-4 pb-16 md:p-6" data-testid="signing-page">
            <PageHeader
                icon={SECTION_ICONS.signatures}
                title={<span data-testid="signing-title">{state.title}</span>}
                description={`${state.organization_name} · ${t('signer.addressed_to', { name: state.signer_name })}${state.signer_role ? ` · ${state.signer_role}` : ''}`}
            />
            {state.message && <p className="whitespace-pre-line rounded-md bg-muted p-3 text-sm text-foreground">{state.message}</p>}

            {/* The PDF stays at this position whatever the panel shows, so it is
                never remounted nor refetched between steps (spec 2026-10-06-02). */}
            <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(320px,2fr)] lg:items-start">
                <section className="min-w-0 space-y-2" aria-label={t('signer.document_label')}>
                    <h2 className="text-base font-semibold text-foreground">{t('signer.step_read')}</h2>
                    <SignerPdf orgCode={orgCode} session={session} name={state.document_name} />
                    <p className="break-all text-xs text-muted-foreground" data-testid="signing-fingerprint">
                        {t('signer.fingerprint', { sha: state.document_sha256 })}
                    </p>
                </section>

                <aside className="space-y-4 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto" data-testid="signing-panel">
                    {state.signer_status === 'signed' && (
                        <Alert data-testid="signing-done">
                            <AppIcon path={mdiCheckCircle} size="sm" />
                            <AlertTitle>{t('signer.signed_title')}</AlertTitle>
                            <AlertDescription>
                                {state.signed_document_available ? t('signer.signed_sealed') : t('signer.signed_waiting')}
                            </AlertDescription>
                        </Alert>
                    )}
                    {state.signer_status === 'declined' && (
                        <Alert variant="warning" data-testid="signing-declined">
                            <AppIcon path={mdiCloseCircleOutline} size="sm" />
                            <AlertTitle>{t('signer.declined_title')}</AlertTitle>
                            <AlertDescription>{t('signer.declined_body')}</AlertDescription>
                        </Alert>
                    )}
                    {state.signer_status !== 'signed' && state.signer_status !== 'declined' && !open && (
                        <Alert variant="warning" data-testid="signing-closed">
                            <AlertTitle>{t('signer.closed_title')}</AlertTitle>
                            <AlertDescription>{t(`request_status.${state.request_status}`)}</AlertDescription>
                        </Alert>
                    )}
                    {state.signed_document_available && (
                        <Button
                            variant="outline"
                            data-testid="signing-download-sealed"
                            onClick={() => run(async () => {
                                const buf = await signerApi.signedDocument(orgCode, session)
                                const url = URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }))
                                const a = document.createElement('a')
                                a.href = url
                                a.download = `${state.document_name || 'document'} (signé).pdf`
                                a.click()
                                URL.revokeObjectURL(url)
                            })}
                        >
                            <AppIcon path={mdiDownload} size="sm" className="mr-2" />
                            {t('signer.download_sealed')}
                        </Button>
                    )}

                    {open && (
                        <>
                            <Card>
                                <CardContent className="pt-6">
                                    <Identification
                                        state={state}
                                        busy={busy}
                                        onSend={() => run(async () => {
                                            const res = await signerApi.sendCode(orgCode, session, lang)
                                            onSession(res.session, res.state)
                                        })}
                                        onVerify={(code) => run(async () => {
                                            const res = await signerApi.verify(orgCode, session, code, lang)
                                            onSession(res.session, res.state)
                                        })}
                                        onPasskey={() => run(async () => {
                                            if (!browserSupportsWebAuthn()) throw new Error(t('signer.passkey_unsupported'))
                                            let answer: { session: string; credential: Record<string, unknown> }
                                            try {
                                                answer = await answerMfaWithPasskey(() => signerApi.beginPasskey(orgCode, session))
                                            } catch (e) {
                                                if (e instanceof SignerApiError) throw e
                                                throw new Error(t('signer.passkey_failed'), { cause: e })
                                            }
                                            const res = await signerApi.verifyPasskey(orgCode, session, answer.session, answer.credential, lang)
                                            onSession(res.session, res.state)
                                        })}
                                        formatDateTime={formatDateTime}
                                    />
                                </CardContent>
                            </Card>
                            {state.verified && (
                                <Card>
                                    <CardContent className="pt-6">
                                        <SignStep
                                            state={state}
                                            busy={busy}
                                            onSign={(handwriting) => run(async () => {
                                                const body = {
                                                    consent: true,
                                                    consent_text_sha256: state.consent_text_sha256,
                                                    handwritten: handwriting.kind === 'drawn'
                                                        ? { kind: 'drawn' as const, strokes: handwriting.strokes }
                                                        : { kind: 'typed' as const, typed: handwriting.typed },
                                                }
                                                onState(await signerApi.sign(orgCode, session, body, lang))
                                            })}
                                        />
                                    </CardContent>
                                </Card>
                            )}
                            <DeclineStep busy={busy} onDecline={(reason) => run(async () => {
                                onState(await signerApi.decline(orgCode, session, reason, lang))
                            })} />
                        </>
                    )}
                    {error && (
                        <Alert variant="destructive" data-testid="signing-error">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                </aside>
            </div>
        </div>
    )
}

/** A numbered step heading of the action panel. */
function StepHeading({ n, children }: { n: number; children: React.ReactNode }) {
    return (
        <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground" aria-hidden="true">{n}</span>
            {children}
        </h2>
    )
}

function SignerPdf({ orgCode, session, name }: { orgCode: string; session: string; name: string }) {
    const { t } = useTranslation('signatures')
    const [mod, setMod] = useState<LoadedPdfModule | null>(null)
    const [data, setData] = useState<Uint8Array | null>(null)
    const [failed, setFailed] = useState(false)
    const [pages, setPages] = useState(0)
    const containerRef = useRef<HTMLDivElement>(null)
    const [width, setWidth] = useState(600)

    // The bytes don't depend on which session fetched them: read the session
    // once, so a rotated one doesn't refetch nor reset the viewer.
    const [firstSession] = useState(session)

    useEffect(() => {
        let cancelled = false
        Promise.all([loadPdfModule(), signerApi.document(orgCode, firstSession)])
            .then(([m, buf]) => {
                if (cancelled) return
                setMod(m)
                setData(new Uint8Array(buf))
            })
            .catch(() => !cancelled && setFailed(true))
        return () => {
            cancelled = true
        }
    }, [orgCode, firstSession])

    useEffect(() => {
        const el = containerRef.current
        if (!el || typeof ResizeObserver === 'undefined') return
        const ro = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect.width
            if (w) setWidth(Math.max(200, Math.floor(w)))
        })
        ro.observe(el)
        return () => ro.disconnect()
    }, [])

    // react-pdf takes ownership of the buffer it is given: hand it a copy,
    // stable across renders.
    const file = useMemo(() => (data ? { data: data.slice() } : null), [data])

    return (
        <div ref={containerRef} className="max-h-[70vh] overflow-y-auto rounded-md border bg-muted/30 p-2 lg:max-h-[calc(100vh-12rem)]" data-testid="signing-pdf" aria-label={name}>
            {failed && <p className="p-4 text-sm text-muted-foreground">{t('signer.pdf_error')}</p>}
            {!failed && (!mod || !file) && <p className="p-4 text-sm text-muted-foreground">{t('signer.pdf_loading')}</p>}
            {!failed && mod && file && (
                <mod.Document
                    file={file}
                    options={mod.documentOptions}
                    suspense={false}
                    onLoadSuccess={({ numPages }) => setPages(numPages)}
                    onLoadError={() => setFailed(true)}
                    loading={<p className="p-4 text-sm text-muted-foreground">{t('signer.pdf_loading')}</p>}
                >
                    {Array.from({ length: pages }, (_, i) => (
                        <mod.Page key={i} pageNumber={i + 1} width={width - 16} className="mb-3 shadow-sm [&>canvas]:mx-auto" loading="" />
                    ))}
                </mod.Document>
            )}
            {pages > 0 && <p className="px-2 text-xs text-muted-foreground" data-testid="signing-pdf-pages">{t('signer.pages', { count: pages })}</p>}
        </div>
    )
}

function Identification({ state, busy, onSend, onVerify, onPasskey, formatDateTime }: {
    state: SignerState
    busy: boolean
    onSend: () => void
    onVerify: (code: string) => void
    onPasskey: () => void
    formatDateTime: (d: string) => string
}) {
    const { t } = useTranslation('signatures')
    const [code, setCode] = useState('')
    if (state.verified) {
        return (
            <section className="space-y-1" data-testid="signing-verified">
                <StepHeading n={1}>{t('signer.step_identify')}</StepHeading>
                <p className="flex items-center gap-2 text-sm text-foreground">
                    <AppIcon path={mdiCheckCircle} size="sm" className="text-success" />
                    {t('signer.identified')}
                </p>
            </section>
        )
    }
    if (state.locked_until) {
        return (
            <Alert variant="warning" data-testid="signing-locked">
                <AppIcon path={mdiLockOutline} size="sm" />
                <AlertTitle>{t('signer.locked_title')}</AlertTitle>
                <AlertDescription>{t('signer.locked_body', { at: formatDateTime(state.locked_until) })}</AlertDescription>
            </Alert>
        )
    }
    const strong = state.identity_level === 'strong'
    const factors = state.strong_factors ?? []
    const passkey = strong && factors.includes('passkey')
    const totp = strong && (factors.includes('totp') || !passkey)
    const strongText = passkey && totp ? 'signer.identify_strong' : passkey ? 'signer.identify_passkey' : 'signer.identify_totp'
    return (
        <section className="space-y-3" data-testid="signing-identify">
            <StepHeading n={1}>{t('signer.step_identify')}</StepHeading>
            <p className="text-sm text-muted-foreground">
                {strong
                    ? t(strongText)
                    : t(state.code_channel === 'sms' ? 'signer.identify_sms' : 'signer.identify_email', { to: state.code_destination })}
            </p>
            {passkey && (
                <Button variant="outline" onClick={onPasskey} disabled={busy} data-testid="signing-use-passkey">
                    {t('signer.use_passkey')}
                </Button>
            )}
            {!strong && (
                <Button variant="outline" onClick={onSend} disabled={busy} data-testid="signing-send-code">
                    {state.code_sent ? t('signer.resend_code') : t('signer.send_code')}
                </Button>
            )}
            {(totp || state.code_sent) && (
                <form
                    className="flex flex-wrap items-end gap-2"
                    onSubmit={(e) => {
                        e.preventDefault()
                        onVerify(code)
                    }}
                >
                    <div className="space-y-1">
                        <Label htmlFor="signing-code">{t('signer.code_label')}</Label>
                        <Input
                            id="signing-code"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={6}
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                            className="w-36 tracking-widest"
                            data-testid="signing-code-input"
                        />
                    </div>
                    <Button type="submit" disabled={busy || code.length !== 6} data-testid="signing-verify">
                        {t('signer.verify')}
                    </Button>
                </form>
            )}
        </section>
    )
}

function SignStep({ state, busy, onSign }: { state: SignerState; busy: boolean; onSign: (h: Handwriting) => void }) {
    const { t } = useTranslation('signatures')
    const [consent, setConsent] = useState(false)
    const [handwriting, setHandwriting] = useState<Handwriting | null>({ kind: 'typed', typed: state.signer_name })
    const ready = consent && isHandwritingReady(handwriting)
    return (
        <section className="space-y-4" data-testid="signing-sign">
            <div className="space-y-2">
                <StepHeading n={2}>{t('signer.step_consent')}</StepHeading>
                <div className="flex items-start gap-3">
                    <Checkbox
                        id="signing-consent"
                        checked={consent}
                        onCheckedChange={(v) => setConsent(v === true)}
                        data-testid="signing-consent"
                    />
                    <Label htmlFor="signing-consent" className="text-sm font-normal leading-snug" data-testid="signing-consent-text">
                        {state.consent_text}
                    </Label>
                </div>
            </div>
            <div className="space-y-2">
                <StepHeading n={3}>{t('signer.step_handwriting')}</StepHeading>
                <p className="text-xs text-muted-foreground">{t('signer.handwriting_hint')}</p>
                <SignaturePad value={handwriting} onChange={setHandwriting} defaultTyped={state.signer_name} />
            </div>
            <Button
                disabled={busy || !ready || !handwriting}
                onClick={() => handwriting && onSign(handwriting)}
                data-testid="signing-submit"
            >
                {t('signer.sign')}
            </Button>
        </section>
    )
}

function DeclineStep({ busy, onDecline }: { busy: boolean; onDecline: (reason: string) => void }) {
    const { t } = useTranslation('signatures')
    const [opened, setOpened] = useState(false)
    const [reason, setReason] = useState('')
    if (!opened) {
        return (
            <Button variant="ghost" className="text-destructive" onClick={() => setOpened(true)} data-testid="signing-decline-open">
                {t('signer.decline')}
            </Button>
        )
    }
    return (
        <section className="space-y-2 rounded-md border border-destructive/40 p-3" data-testid="signing-decline">
            <Label htmlFor="signing-decline-reason">{t('signer.decline_reason')}</Label>
            <Textarea
                id="signing-decline-reason"
                value={reason}
                maxLength={2000}
                onChange={(e) => setReason(e.target.value)}
                data-testid="signing-decline-reason"
            />
            <div className="flex gap-2">
                <Button variant="destructive" disabled={busy || !reason.trim()} onClick={() => onDecline(reason)} data-testid="signing-decline-confirm">
                    {t('signer.decline_confirm')}
                </Button>
                <Button variant="outline" onClick={() => setOpened(false)} data-testid="signing-decline-cancel">
                    {t('signer.cancel')}
                </Button>
            </div>
        </section>
    )
}
